import { Check, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Money, StatusPill } from '@/shared/ui';
import { cn } from '@/shared/utils/cn';
import { formatNumber } from '@/shared/utils/number';

import { rememberProductImage, rememberedProductImage } from '../lib/productImage';
import type { OfflineProduct } from '../types';

import { OfflineProductThumb } from './OfflineProductThumb';

export interface OfflineProductCardProps {
  product: OfflineProduct;
  exactMatch?: boolean;
  disabled?: boolean;
  onAdd: (productId: string) => void;
}

export function OfflineProductCard({
  product,
  exactMatch = false,
  disabled = false,
  onAdd,
}: OfflineProductCardProps) {
  const { t } = useTranslation('offlineOrders');
  const promo = product.customerSellingPricePaise < product.normalSellingPricePaise;

  useEffect(() => {
    if (product.primaryImageUrl) rememberProductImage(product.productId, product.primaryImageUrl);
  }, [product.primaryImageUrl, product.productId]);

  return (
    <article className="flex gap-3 rounded-md border border-border bg-surface p-3">
      <OfflineProductThumb
        src={product.primaryImageUrl}
        fallbackSrc={rememberedProductImage(product.productId)}
        alt={product.name}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-fg">{product.name}</h3>
            {product.unit ? <p className="text-xs text-fg-muted">{product.unit}</p> : null}
          </div>
          {exactMatch ? (
            <StatusPill tone="info" icon={Check}>
              {t('search.exactMatch')}
            </StatusPill>
          ) : null}
        </div>

        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
          <span
            className={cn('font-semibold tabular-nums', promo ? 'text-success-strong' : 'text-fg')}
          >
            <Money amount={product.customerSellingPricePaise} />
          </span>
          {promo ? (
            <span className="text-xs text-fg-muted">
              {t('search.chargeable')} <Money amount={product.normalSellingPricePaise} />
            </span>
          ) : null}
          {product.mrpPaise > 0 ? (
            <span className="text-xs text-fg-muted">
              {t('search.mrp')} <Money amount={product.mrpPaise} />
            </span>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill
              tone={product.inStock ? 'success' : 'danger'}
              icon={product.inStock ? Check : XCircle}
            >
              {product.inStock ? t('search.inStock') : t('search.outOfStock')}
            </StatusPill>
            <span className="text-xs text-fg-muted">
              {t('search.stock', { quantity: formatNumber(product.quantity) })}
            </span>
          </div>
          <Button
            type="button"
            size="sm"
            disabled={disabled}
            onClick={() => onAdd(product.productId)}
          >
            {t('search.add')}
          </Button>
        </div>
      </div>
    </article>
  );
}

export function OfflineQuantityControl({
  quantity,
  disabled,
  onCommit,
}: {
  quantity: number;
  disabled?: boolean;
  onCommit: (quantity: number) => void;
}) {
  const { t } = useTranslation('offlineOrders');
  const [draft, setDraft] = useState(String(quantity));

  useEffect(() => {
    setDraft(String(quantity));
  }, [quantity]);

  const commit = () => {
    const parsed = Number(draft);
    if (!Number.isInteger(parsed)) {
      setDraft(String(quantity));
      return;
    }
    if (parsed !== quantity) onCommit(parsed);
  };

  return (
    <div className="flex items-center gap-1">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="w-8 shrink-0 px-0"
        disabled={disabled}
        aria-label={t('cart.decrease')}
        onClick={() => onCommit(quantity - 1)}
      >
        −
      </Button>
      <input
        value={draft}
        inputMode="numeric"
        aria-label={t('cart.quantity')}
        disabled={disabled}
        onChange={(event) => setDraft(event.target.value.replace(/[^\d]/g, ''))}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
        }}
        className={cn(
          'h-8 w-12 shrink-0 rounded-sm border border-border-strong bg-surface text-center text-sm tabular-nums text-fg',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        )}
      />
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="w-8 shrink-0 px-0"
        disabled={disabled}
        aria-label={t('cart.increase')}
        onClick={() => onCommit(quantity + 1)}
      >
        +
      </Button>
    </div>
  );
}
